import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useOrderedBoards } from '../../hooks/useBoards';
import { useT } from '../../i18n';
import { haptic } from '../../native/platform';
import { ofBoard, openCards, useCards } from '../../store/cards';
import { useSession } from '../../store/session';
import { useUi } from '../../store/ui';
import type { BoardInfo } from '../../sync/api';
import styles from './BoardTabs.module.css';

const TABS_MAX = 4;

/** Choosing a board: the board slides in from the side of the list it sits on. */
export function usePickBoard(boards: BoardInfo[]): (id: string) => void {
  const boardId = useSession((s) => s.boardId);
  const setBoard = useSession((s) => s.setBoard);
  const slideBoard = useUi((s) => s.slideBoard);
  return (id: string) => {
    if (id === boardId) return;
    const from = boards.findIndex((b) => b.id === boardId);
    const to = boards.findIndex((b) => b.id === id);
    setBoard(id);
    haptic('tick');
    slideBoard(to > from ? 1 : -1);
  };
}

/**
 * One click per board, on the phone's bottom bar and the desktop's dock alike, while there
 * are four or fewer and every name reads in full; past that, the fallback the caller gives,
 * the board's name with its list. The tabs are measured out of sight on every resize.
 */
export function BoardTabs({ size, fallback, title }: { size: 'bar' | 'dock'; fallback: ReactNode; title?: string }) {
  const t = useT();
  const boards = useOrderedBoards();
  const boardId = useSession((s) => s.boardId);
  const cards = useCards((s) => s.cards);
  const pick = usePickBoard(boards);
  const boxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [tabs, setTabs] = useState(boards.length <= TABS_MAX);
  const count = (id: string) => openCards(ofBoard(cards, id)).length;

  useLayoutEffect(() => {
    const box = boxRef.current;
    const measure = measureRef.current;
    if (!box || !measure) return;
    const check = () => setTabs(boards.length <= TABS_MAX && measure.offsetWidth <= box.clientWidth);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    observer.observe(box);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [boards]);

  return (
    <div ref={boxRef} className={`${styles.box} ${styles[size]}`}>
      {/* Hidden or not, anything wider than the screen would let the page scroll sideways: the box clips it. */}
      <div className={styles.measureBox} aria-hidden="true">
        <div ref={measureRef} className={`${styles.tabs} ${styles.measure}`}>
          {boards.map((b) => (
            <span key={b.id} className={styles.tab}>
              <span className={styles.tabName}>{b.name}</span>
              <span className={styles.tabCount}>{count(b.id)}</span>
            </span>
          ))}
        </div>
      </div>
      {tabs ? (
        <div className={styles.tabs} role="group" aria-label={t.t('boards.title')} title={title}>
          {boards.map((b) => (
            <button
              key={b.id}
              type="button"
              className={styles.tab}
              aria-current={b.id === boardId ? 'true' : undefined}
              aria-label={`${b.name}, ${t.tn('count.cards', count(b.id))}`}
              data-board-tab={b.id}
              onClick={() => pick(b.id)}
            >
              <span className={styles.tabName}>{b.name}</span>
              <span className={styles.tabCount}>{count(b.id)}</span>
            </button>
          ))}
        </div>
      ) : (
        fallback
      )}
    </div>
  );
}
